import { Injectable } from '@angular/core';
import {
  ACCOUNT_TEMPLATE_TYPES,
  AccountViewModel,
  IAccountResponse,
} from '@models/account.class';
import {
  ContractViewModel,
  IBuyingPower,
  IContractAccountsCollection,
  IManagementType,
} from '@models/contract.class';
import {
  IPositionItem,
  IPositionSummaryResponse,
  PortfolioTotals,
} from '@models/position/positionSummary.class';
import { SearchBoxIssueItem } from '@models/search-box-issue-item';
import { TimeWeightedReturn } from '@models/time-weighted-return.class';
import { Store } from '@ngrx/store';
import { ContractManagementService } from '@services/contractManagement.service';
import { PortfolioService } from '@services/portfolio.service';
import { fromJS, Map } from 'immutable';
import { isEqual } from 'lodash';
import {
  combineLatest,
  EMPTY,
  forkJoin as observableForkJoin,
  interval as observableInterval,
  Observable,
  of,
  Subject,
  Subscription,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  CONTRACT_CONFIGURATION,
  CURRENT_SYMBOL,
  DEFAULT_STRATEGY_NAME,
  USER_ROLES,
} from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import { ContractsReceived } from 'src/app/state/actions/contracts-com.action';
import { AppState } from 'src/app/state/app-state';
import {
  CONTRACTS,
  CURRENT_CONTRACT,
  CURRENT_CONTRACT_CONTRACTID,
  CURRENT_CONTRACT_LOREM_ACCOUNT,
  CURRENT_CONTRACT_LOREM_ACCOUNT_MIGRATED,
  CURRENT_CONTRACT_PROPERTIES,
  CURRENT_CONTRACT_TOTALS,
  CURRENT_USER_GLOBAL,
  EMPTY_CONTRACTS,
  LOREM_ACCOUNTS,
  PARTY_ID,
  POSITION,
  POSITION_IS_PROCESSING,
  TIME_WEIGHTED_RETURN,
} from 'src/app/state/app.reducer';
import { NotificationService } from 'src/core/services/notification.service';
import { OperationService } from 'src/core/services/operation.service';
import { PositionService } from 'src/core/services/position.service';
import { SessionService } from 'src/core/services/session.service';
import { SharedSolaceStreamService } from 'src/solace';
import { SearchService } from './search.service';

@Injectable()
export class ContractsService {
  positionTimestamp: any = {
    last: new Date().getTime(),
  };

  private intervalTimeOut: number = 30 * 1000;
  private intervalPositionObservable$: Observable<any>;

  private loggedInSubscription: Subscription;
  private intervalPositionSubscription: Subscription;
  private internalPositionSubscription: Subscription;
  private currentContract: any;
  private isCurrentPosition = true;

  private isLoggedIn = false;
  private contracts: any[];
  private onDestroyInterval$ = new Subject<void>();

  private lastContractManagementType: IManagementType;

  constructor(
    private store: Store<AppState>,
    private contractManagementService: ContractManagementService,
    private portfolioService: PortfolioService,
    private notificationService: NotificationService,
    private positionService: PositionService,
    private operationService: OperationService,
    private searchService: SearchService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private readonly sessionService: SessionService,
  ) {
    this.init();
  }

  getContractsBP(): Observable<any> {
    return this.contractManagementService.getContractsBP().pipe(
      tap((contracts) => {
        this.setCurrentContract(contracts);
      }),
    );
  }

  setCurrentContract(contracts: any[]) {
    this.store
      .select('userRole')
      .pipe(
        filter((userRole) => userRole !== undefined),
        catchError((error: any) => {
          this.notificationService.showServiceError(error);
          return EMPTY;
        }),
        take(1),
      )
      .subscribe(
        (userRole: any) => {
          const isAdvisor = userRole === USER_ROLES.ADVISOR;

          const activeContracts = contracts
            .filter((c: any) => {
              return c.isEnabled;
            })
            .filter((c: any) => {
              return c.manageType !== IManagementType.USA;
            })
            .map((contract) => ({
              ...contract,
              strategyName: DEFAULT_STRATEGY_NAME,
            }));

          if (isAdvisor) {
            activeContracts.sort((a: any, b: any) => {
              return a.isGlobal === b.isGlobal ? 0 : a.isGlobal ? -1 : 1;
            });
          }

          /**
           * TODO:
           * This assignation to a local array is not a good approach
           * since it could ended up in a race condition.
           * However the rest of the methods that use it
           * are only available once the application finished loading.
           * The local array must be removed
           * and used "this.store.select('contracts')" where needed.
           */
          this.contracts = [...activeContracts];
          this.store.dispatch({
            type: CONTRACTS,
            payload: activeContracts,
          });

          this.store.dispatch({
            type: POSITION_IS_PROCESSING,
            payload: false,
          });

          const defaultContractInConfig =
            this.sessionService.getCurrentContract();

          const defaultContracts = activeContracts.filter((contract: any) => {
            return defaultContractInConfig === CONTRACT_CONFIGURATION.DEFAULT
              ? contract.isDefault
              : contract.contractId === defaultContractInConfig;
          });

          const [defaultContract] =
            !isAdvisor && defaultContracts.length > 0
              ? defaultContracts
              : activeContracts;

          if (defaultContract) {
            this.getContractInfo(defaultContract.contractId)
              .pipe(catchError(() => EMPTY))
              .subscribe();
            this.setEmptyContractState(false);
          } else {
            this.setEmptyContractState(true);
          }
        },
        () => undefined,
      );
  }

  setEmptyContractState(state: boolean) {
    this.store.dispatch({
      type: EMPTY_CONTRACTS,
      payload: state,
    });
  }

  getContractInfo(contractId: string): Observable<boolean> {
    this.store.dispatch({
      type: CURRENT_CONTRACT_CONTRACTID,
      payload: contractId,
    });

    const contract = this.contracts.find(
      (item: any) => item.contractId === contractId,
    );

    return this.contractManagementService.getContract(contractId).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      map((result: any) => {
        let newResult = false;
        if (!result || result === null) {
          const errorMessage = `El contrato ${contractId} tiene un problema para
              operar en homebroker, favor de contactar a un asesor.`;

          const error: any = {
            EventId: 10001,
            ErrorMessage: errorMessage,
            IsBussinessError: true,
          };

          this.notificationService.showServiceError(error);

          newResult = false;
        } else {
          this.cleanPortfolioData();
          this.setContractType(result);

          if (contract === undefined) {
            result.isLoremAccount = false;
            result.isLoremAccountMigrated = false;
          } else {
            result.isReadOnlyByRelationship = contract.isReadOnlyByRelationship;
          }

          this.generateAvailableAccounts(result.loremContractId);

          this.store.dispatch({
            type: CURRENT_CONTRACT,
            payload: {
              ...result,
              isUsa: contract
                ? contract.manageType === IManagementType.USA
                : false,
            },
          });

          this.store.dispatch({
            type: TIME_WEIGHTED_RETURN,
            payload: fromJS({}),
          });

          // Only reset the puppet when switching between management types (USA/MX)
          if (this.lastContractManagementType !== contract.manageType) {
            this.store.dispatch({
              type: CURRENT_SYMBOL,
              payload: { clearSearchBox: true },
            });
          }

          this.lastContractManagementType = contract.manageType;
          newResult = true;
        }
        return newResult;
      }),
    );
  }

  getPositionSummary(
    contractId: string,
    isTimer?: boolean,
    type: string = '',
    processDate: string = '',
    isRealTime?: boolean,
  ): Observable<any> {
    if (this.intervalPositionSubscription) {
      this.intervalPositionSubscription.unsubscribe();
    }

    if (this.internalPositionSubscription) {
      this.internalPositionSubscription.unsubscribe();
    }

    if (!this.isLoggedIn) {
      return EMPTY;
    } else {
      if (contractId !== this.currentContract) {
        this.getPositionSummary(this.currentContract);
      }

      // TODO: any is incorrect, but typing it as IPositionSummaryResponse produces conflicts
      // because the original observables that get assigned in the following if-tree
      // returns a plain "Response" object
      let observablePosition$: Observable<any>;

      // if ((!processDate && type === '') || (processDate && type === '')) {
      if (type === '' || type === 'realTime') {
        this.isCurrentPosition = true;
        observablePosition$ = this.portfolioService.getPositionSummary(
          contractId,
          isTimer,
          isRealTime,
        );
      } else if (type === 'initial') {
        this.isCurrentPosition = false;
        observablePosition$ = this.portfolioService.getInitialPosition(
          contractId,
          processDate,
        );
      } else if (type === 'close') {
        this.isCurrentPosition = false;
        observablePosition$ = this.portfolioService.getClosePosition(
          contractId,
          processDate,
        );
      }

      const observableContractProperties$: Observable<any> =
        this.operationService.getContractProperties(
          contractId,
          isTimer,
          isRealTime,
        );

      const observableContractBuyingPower$: Observable<any> =
        this.contractManagementService.getContractBuyingPower(
          contractId,
          isRealTime,
        );
      const getPositionObservable$: Observable<any> = observableForkJoin(
        observablePosition$
          .pipe(map((position) => this.injectWeightedAveragePrices(position)))
          .pipe(catchError(() => EMPTY)),
        observableContractProperties$.pipe(catchError(() => EMPTY)),
        observableContractBuyingPower$.pipe(catchError(() => EMPTY)),
        (
          position: IPositionSummaryResponse,
          contractProperties: any,
          buyingPowerData: any,
        ) => {
          buyingPowerData && this.checkIfBuyingPowerIsMocked(buyingPowerData);
          position && this.checkIfPositionIsMocked(position);

          this.store.dispatch({
            type: POSITION,
            payload: position,
          });

          this.store.dispatch({
            type: CURRENT_CONTRACT_PROPERTIES,
            payload: contractProperties,
          });
          return { position, contractProperties, buyingPowerData };
        },
      ).pipe(
        debounceTime(500),
        mergeMap((info: any) => {
          return this.getMargin(isTimer).pipe(
            map((margin: any) => {
              const totals: PortfolioTotals =
                this.positionService.getTotalsResume(
                  info.position,
                  margin.capitalMarketRisks,
                  margin.margin,
                  info.contractProperties.sellingPower,
                  info.buyingPowerData,
                );

              this.store.dispatch({
                type: CURRENT_CONTRACT_TOTALS,
                payload: totals,
              });
            }),
          );
        }),
      );

      return getPositionObservable$.pipe(take(1));
    }
  }

  public getPositionSummaryBatch(
    contractId?: string,
    isRealTime: boolean = false,
  ): void {
    const empty = '';
    this.getPositionSummary(
      contractId || this.currentContract,
      false,
      empty,
      empty,
      isRealTime,
    )
      .pipe(
        catchError(() => {
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  unsubscribePositionSummaryLoop() {
    this.intervalPositionSubscription &&
      this.intervalPositionSubscription.unsubscribe();
  }

  /**
   * Get historical closure position in specific {processDate} of {contractId}
   *
   * @param startDate Initial date.
   * @param endDate Final date.
   * @param [contractId] The user's contract ID. Defaults to currentContract
   * @param [issueId] (Optional) The related issue ID
   */

  getHistoricalPosition(
    startDate: string,
    endDate: string,
    contractId: string = this.currentContract,
    issueId: string = '',
  ) {
    const observableResponse$: Observable<any> =
      this.portfolioService.getHistoricalClosurePosition(
        contractId,
        startDate,
        endDate,
        issueId,
      );

    observableResponse$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
      take(1),
    );

    return observableResponse$;
  }

  /**
   * Get the time weighted return of specific {contractId}
   *
   * @param contractId The user's contract ID
   */

  getTimeWeightedReturn(
    contractId: string = this.currentContract,
  ): Observable<TimeWeightedReturn> {
    return this.portfolioService.getTimeWeightedReturn(contractId).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
    ) as any as Observable<TimeWeightedReturn>;
  }

  /**
   * historical portfolio position of specific {contractId} for chart usage
   *
   * @param contractId The user's contract ID
   * @param startDate Initial date.
   * @param endDate Final date.
   */

  getHistoricalPortfolioPosition(
    startDate: string,
    endDate: string,
    contractId: string = this.currentContract,
  ) {
    return this.portfolioService
      .getHistoricalPortfolioPosition(contractId, startDate, endDate)
      .pipe(
        catchError((error) => {
          this.notificationService.showServiceError(error);
          return observableThrowError(error);
        }),
      );
  }

  /**
   * Get contract Yield by range in specific contractId
   * @param info Contains: contractId, beginDate, endDate and param of filter
   */
  getportfolioYield(info: any): Observable<any> {
    return this.portfolioService.getportfolioYield(info).pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return observableThrowError(error);
      }),
    );
  }

  searchContract(contractQuery: string) {
    const observableResponse$: Observable<any> =
      this.contractManagementService.searchContracts(contractQuery);

    observableResponse$.pipe(
      catchError((error: any) => {
        this.notificationService.showServiceError(error);

        return EMPTY;
      }),
    );

    return observableResponse$;
  }

  public getAccountManagerElegibility() {
    this.operationService
      .getAccountManagerElegibility()
      .pipe(
        catchError((error: any) => {
          this.fillPartyResult({ error: 'error' });
          return of(error);
        }),
      )
      .subscribe((value: any) => {
        this.fillPartyResult(value);
      });
  }

  public getContractOpening() {
    return this.contractManagementService.getContractOpening().pipe(
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  public getTradingGlobalAccount(accountId: string) {
    return this.contractManagementService
      .getTradingGlobalAccount(accountId)
      .pipe(
        catchError(() => {
          return EMPTY;
        }),
      );
  }

  public isLoremAccount(loremAccounts: any[], contractID: string): any {
    let loremAccount;
    if (loremAccounts.length && contractID) {
      for (const account of loremAccounts) {
        if (contractID.search(account.legacy_contract_id) > -1) {
          account.is_migrated = account.legacy_contract_id === contractID;
          account.contract_id = contractID;
          loremAccount = account;
          break;
        }
      }
    }
    return loremAccount;
  }

  public getAllContractsProperties(contracts: string): Observable<any> {
    const observableResponse$: Observable<any> =
      this.contractManagementService.getAllContractsProperties(contracts);
    return observableResponse$.pipe(
      catchError((error) => this.handleError(error)),
    );
  }

  public getAccounts(contractId: string): Observable<AccountViewModel[]> {
    return this.contractManagementService
      .getAccounts(contractId)
      .pipe(
        map((accounts: any) =>
          accounts
            .filter(
              (account) =>
                account.management_type_template !==
                ACCOUNT_TEMPLATE_TYPES.TRADING_USA,
            )
            .map((account: IAccountResponse) =>
              AccountViewModel.fromJSON(account),
            ),
        ),
      );
  }

  public patchAccount(
    contractId: string,
    accountId: string,
    account: any,
  ): Observable<any> {
    return this.contractManagementService
      .patchAccount(contractId, accountId, account)
      .pipe(catchError((error: any) => this.handleError(error.error)));
  }

  public generateAvailableAccounts(contractId: string): void {
    let contractAccountsCollection: IContractAccountsCollection;
    const contractsCom: ContractViewModel[] = [];

    /**
     * Return if contract doesn't have a valid loremContractId
     */
    if (contractId.match(/0{12}/g)) {
      this.cleanContractAccountsCollection();
      return;
    }

    this.getAccounts(contractId)
      .pipe(
        tap((contractWithAccounts) => {
          const contractCom = new ContractViewModel(
            contractId,
            contractWithAccounts,
          );
          if (contractWithAccounts) {
            !contractAccountsCollection &&
              (contractAccountsCollection = {} as IContractAccountsCollection);
            const {
              contracts: updatedContracts,
              accounts: updatedAccounts,
              accountRelation: updatedAccountRelation,
            } = contractCom.getContractAccountsCollection();

            if (!contractAccountsCollection.contracts) {
              contractAccountsCollection.contracts = contractsCom.reduce(
                (collection, contract) => {
                  collection[contract.contractId] = [];
                  return collection;
                },
                {} as any,
              );
            }
            const { contracts, accounts, accountRelation } =
              contractAccountsCollection;

            contractAccountsCollection.contracts = {
              ...contracts,
              ...updatedContracts,
            };

            contractAccountsCollection.accounts = {
              ...accounts,
              ...updatedAccounts,
            };

            contractAccountsCollection.accountRelation = {
              ...accountRelation,
              ...updatedAccountRelation,
            };

            this.store.dispatch(
              new ContractsReceived(contractAccountsCollection),
            );
          }
        }),
      )
      .subscribe(
        () => undefined,
        () => this.cleanContractAccountsCollection(),
      );
  }

  private init(): void {
    this.loggedInSubscription = this.store
      .select('loggedIn')
      .subscribe((value: boolean) => {
        this.isLoggedIn = value;
        if (value) {
          this.intervalTimeOut =
            +featureFlags.positionPollTimer.getValue() * 1000;
          this.intervalPositionObservable$ = observableInterval(
            this.intervalTimeOut,
          ).pipe(takeUntil(this.onDestroyInterval$));
        } else {
          if (this.intervalPositionSubscription) {
            this.intervalPositionSubscription.unsubscribe();
          }

          if (this.internalPositionSubscription) {
            this.internalPositionSubscription.unsubscribe();
          }
        }
      });

    combineLatest(
      this.store
        .select('currentContract')
        .pipe(
          map((value: any) => value.toJS()),
          filter(
            (currentContract) => currentContract && currentContract.contractID,
          ),
        )
        .pipe(distinctUntilKeyChanged('contractID')),
      this.store.select('loremAccounts').pipe(
        map((value: any) => value.toJS()),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      ),
    ).subscribe(
      ([currentContract, loremAccounts]) => {
        this.currentContract = currentContract.contractID;
        if (loremAccounts.loaded) {
          const loremAccount = this.isLoremAccount(
            loremAccounts.data,
            currentContract.contractID,
          );
          const isLoremAccount = !!loremAccount;
          const legacyContractId = isLoremAccount
            ? loremAccount?.legacy_contract_id
            : currentContract.contractID;
          this.store.dispatch({
            type: CURRENT_CONTRACT,
            payload: {
              ...currentContract,
              legacyContractId,
            },
          });
          this.store.dispatch({
            type: CURRENT_CONTRACT_LOREM_ACCOUNT,
            payload: isLoremAccount,
          });

          this.store.dispatch({
            type: CURRENT_CONTRACT_LOREM_ACCOUNT_MIGRATED,
            payload: !!loremAccount && loremAccount.is_migrated,
          });
        }
      },
      () => {
        // Do nothing, just catch error
      },
    );
  }

  private cleanPortfolioData(): void {
    this.store.dispatch({
      type: POSITION,
      payload: fromJS([]),
    });

    if (this.intervalPositionSubscription) {
      this.intervalPositionSubscription.unsubscribe();
    }

    if (this.internalPositionSubscription) {
      this.internalPositionSubscription.unsubscribe();
    }
  }

  private getMargin(isTimer?: boolean): Observable<any> {
    const getMargin$: Observable<any> = this.store
      .select('currentContract')
      .pipe(
        filter((mapShadowed: any) => Map.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
        take(1),
        mergeMap((contract: any) => {
          if (contract.tradeMargin) {
            return this.operationService
              .getCapitalMarketContractRisk(contract.contractID, isTimer)
              .pipe(
                map((risk: any) => {
                  let newMargin: number = contract.properties.marginHB;

                  newMargin -= risk.registeredOrdersValue;
                  newMargin -= risk.pendingOrdersRisk;
                  newMargin -= risk.reportos;
                  newMargin -= risk.virtualSalesGBMF2;

                  return {
                    capitalMarketRisks: risk,
                    margin: newMargin,
                  };
                }),
              );
          } else {
            return of({
              capitalMarketRisks: contract.properties.contractRisk,
              margin: contract.properties.marginHB,
            });
          }
        }),
      );

    return getMargin$;
  }

  private setContractType(contract: any) {
    const payload: boolean = contract.clientType === 2;

    this.store.dispatch({
      type: CURRENT_USER_GLOBAL,
      payload,
    });
  }

  private fillPartyResult(response: any) {
    let partyId = 'PartyNotFound';
    if (response.is_account_manager_eligible) {
      partyId = response.party_id;
      this.store.dispatch({
        type: LOREM_ACCOUNTS,
        payload: response.contract_objects,
      });
    } else {
      this.store.dispatch({
        type: LOREM_ACCOUNTS,
        payload: [],
      });
    }

    this.store.dispatch({
      type: PARTY_ID,
      payload: partyId,
    });
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);

    return observableThrowError(error);
  }

  private checkIfPositionIsMocked({
    efectivo,
    totalPortfolioValue,
  }: IPositionSummaryResponse) {
    if (!efectivo || !totalPortfolioValue) {
      return;
    }

    if (
      efectivo.some((e) => e.instrumentType === -1 && e.marketValue === -1) &&
      totalPortfolioValue.some(
        (e) => e.instrumentType === -1 && e.marketValue === -1,
      )
    ) {
      this.store.dispatch({
        type: 'SERVICE_INTERRUPTION',
        payload: { position: true },
      });
    }
  }

  private checkIfBuyingPowerIsMocked({
    buyingPower,
    marketValueTotal,
  }: IBuyingPower) {
    if (buyingPower === -1 && marketValueTotal === -1) {
      this.store.dispatch({
        type: 'SERVICE_INTERRUPTION',
        payload: { buyingPower: true },
      });
    }
  }

  /**
   * Injects the correct Weighted Average Price into the NAC/SIC items in position
   *
   * Due to deprecation of DBFeed, the original endpoint stopped returning
   * a weightedAveragePrice (WAP), and instead returns 0.
   *
   * We can wet the correct WAP from GBMCOM/v1/markets, so we take that
   * value and replace it into the position data.
   *
   * @param position that may contain mercadoCapitales (NAC) or mercadosGlobalesSIC (SIC)
   */
  private injectWeightedAveragePrices(position: IPositionSummaryResponse) {
    // This will create a "callback" using this SearchService instance, so
    // when issues get an update via solace, it also replicates those updates
    // into the searchService.issues, and they'll be up to date whenever this
    // method gets called again (every time position gets updated).
    if (!this.sharedSolaceStreamService.isSearchServiceUpdaterCreated) {
      this.sharedSolaceStreamService.createSearchServiceUpdater(
        this.searchService,
      );
    }

    // When position doesn't exist, or it doesn't contain any NAC/SIC items
    if (
      !position ||
      (!position.mercadoCapitales && !position.mercadosGlobalesSIC)
    ) {
      return position;
    }

    const averagePrices = this.getWeightedAveragePrices(
      this.searchService.issues,
    );

    let newPosition = { ...position };

    if (position.mercadoCapitales) {
      newPosition = {
        ...newPosition,
        mercadoCapitales: position.mercadoCapitales.map((item) =>
          this.replaceWeightedAveragePriceFromDict(item, averagePrices),
        ),
      };
    }

    if (position.mercadosGlobalesSIC) {
      newPosition = {
        ...newPosition,
        mercadosGlobalesSIC: position.mercadosGlobalesSIC.map((item) =>
          this.replaceWeightedAveragePriceFromDict(item, averagePrices),
        ),
      };
    }

    return newPosition;
  }

  /**
   * Uses the WAP from the averagePrices dictionary if the IssueId exists on it
   *
   * @param item should be an item from NAC or SIC
   * @param averagePrices should map every IssueId to its WAP
   * @returns the original item with the WAP set in averagePrices
   */
  private replaceWeightedAveragePriceFromDict(
    item: IPositionItem,
    averagePrices: Record<string, number>,
  ): IPositionItem {
    return {
      ...item,
      weightedAveragePrice: averagePrices.hasOwnProperty(item.issueId)
        ? averagePrices[item.issueId]
        : item.weightedAveragePrice,
    };
  }

  /**
   * Creates an object mapping issueIds with their weightedAveragePrices
   *
   * The keys contain all the market issues (both NAC and SIC).
   */
  private getWeightedAveragePrices(
    issues: SearchBoxIssueItem[],
  ): Record<string, number> {
    return issues.reduce(
      (acc, issue) => ({
        ...acc,
        [issue.issueID]: issue.weightedAveragePrice,
      }),
      {},
    );
  }

  private cleanContractAccountsCollection(): void {
    this.store.dispatch({
      type: 'contractAccountsCollection',
      payload: {},
    });
  }
}
